/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: CertificateForm.tsx
 🗓️ Created: 2026-05-02
 🧩 Description:
    Certificate form — supports Self-Signed, Let's Encrypt (HTTP-01 & DNS-01 manual), Custom.
────────────────────────────────────────────────────────────────*/


import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Shield, Plus, AlertTriangle, Copy, Check, Clock, Loader2 } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { FixedActionBar } from '../../components/ui/FixedActionBar';
import { ModuleFormHeader } from '../../components/layouts';
import { FormBox } from '../../components/ui/FormBox';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import Select from '../../components/ui/select';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { CERTIFICATE_TYPE_OPTIONS, CertificateType } from './types';
import { SanTagInput } from './components/SanTagInput';
import { CertInfoBox } from './components/CertInfoBox';
import { CertSelfSignedBox } from './components/CertSelfSignedBox';
import { CertLetsEncryptBox } from './components/CertLetsEncryptBox';
import { CertCustomBox } from './components/CertCustomBox';
import { useCertificateForm } from './hooks/useCertificateForm';

export default function CertificateForm() {
    const { uuid } = useParams<{ uuid: string }>();
    const { t } = useI18n();
    const [copiedDomain, setCopiedDomain] = useState(false);
    const [copiedValue, setCopiedValue]   = useState(false);

    const {
        formData, errors, certInfo, certRecord, dnsChallenge,
        isWaitingForChallenge,
        loading, isSaving, isDeleting, isConfirmingDns, validating,
        showDeleteModal, showPrivateKey, isEditMode,
        handleChange, handleCommonNameChange,
        handleValidateCertificate, handlePreviewSelfSigned,
        handleSave, handleCancel, handleDeleteClick, handleDeleteConfirm,
        handleConfirmDns,
        setShowDeleteModal, setShowPrivateKey, goToList, goToNew,
    } = useCertificateForm(uuid);

    const copyToClipboard = async (text: string, which: 'domain' | 'value') => {
        await navigator.clipboard.writeText(text);
        if (which === 'domain') {
            setCopiedDomain(true);
            setTimeout(() => setCopiedDomain(false), 2000);
        } else {
            setCopiedValue(true);
            setTimeout(() => setCopiedValue(false), 2000);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-muted-foreground">{t('common.loading')}</div>
            </div>
        );
    }

    return (
        <div className="p-6 pb-20">
            <ModuleFormHeader
                icon={<Shield className="h-5 w-5 text-primary" />}
                title={isEditMode ? t('certificates.editCertificate') : t('certificates.createCertificate')}
                backButtonText={t('certificates.actions.list')}
                onBack={goToList}
                actions={[{ icon: <Plus className="h-5 w-5 text-muted-foreground" />, tooltip: t('certificates.actions.add'), onClick: goToNew }]}
            />

            <div className="mt-8 space-y-4">
                {/* ── Waiting-for-Challenge Banner ── */}
                {isWaitingForChallenge && (
                    <div className="rounded-xl border border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-950/40 overflow-hidden shadow-sm">
                        <div className="px-5 py-4 flex items-center gap-3">
                            <Loader2 className="w-5 h-5 text-blue-600 dark:text-blue-400 animate-spin flex-shrink-0" />
                            <div>
                                <p className="font-semibold text-blue-800 dark:text-blue-200 text-sm">
                                    {certRecord?.lastAction === 'restarting_challenge'
                                        ? t('certificates.dnsChallenge.restartingTitle')
                                        : t('certificates.dnsChallenge.generatingTitle')}
                                </p>
                                <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">
                                    {t('certificates.dnsChallenge.generatingSub')}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* ── DNS-01 Challenge Panel ── */}
                {dnsChallenge && (
                    <div className="rounded-xl border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 overflow-hidden shadow-sm">
                        {/* Header */}
                        <div className="bg-amber-100 dark:bg-amber-900/50 px-5 py-3 flex items-center gap-3 border-b border-amber-200 dark:border-amber-700">
                            <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                            <div>
                                <p className="font-semibold text-amber-800 dark:text-amber-200 text-sm">
                                    {t('certificates.dnsChallenge.actionRequired')}
                                </p>
                                <p className="text-xs text-amber-600 dark:text-amber-400">
                                    {t('certificates.dnsChallenge.actionRequiredSub')}
                                </p>
                            </div>
                        </div>

                        <div className="p-5 space-y-4">
                            {/* Record table */}
                            <div className="rounded-lg border border-amber-200 dark:border-amber-700 overflow-hidden text-sm">
                                <div className="grid grid-cols-[80px_minmax(0,1fr)_minmax(0,1.2fr)] bg-amber-100 dark:bg-amber-900/60 px-4 py-2 font-semibold text-amber-700 dark:text-amber-300 text-xs uppercase tracking-wide">
                                    <span>{t('certificates.dnsChallenge.type')}</span>
                                    <span>{t('certificates.dnsChallenge.name')}</span>
                                    <span>{t('certificates.dnsChallenge.value')}</span>
                                </div>
                                <div className="grid grid-cols-[80px_minmax(0,1fr)_minmax(0,1.2fr)] px-4 py-3 items-center gap-2 bg-white dark:bg-amber-950/20">
                                    <span className="font-mono font-bold text-amber-700 dark:text-amber-300">TXT</span>

                                    {/* Domain cell */}
                                    <div className="flex items-center gap-2 min-w-0 pr-4">
                                        <code className="text-xs bg-amber-100 dark:bg-amber-900/50 px-2 py-1 rounded truncate block w-full">
                                            {dnsChallenge.domain}
                                        </code>
                                        <button
                                            type="button"
                                            onClick={() => copyToClipboard(dnsChallenge.domain, 'domain')}
                                            className="text-amber-500 hover:text-amber-700 dark:hover:text-amber-300 flex-shrink-0 transition-colors"
                                            title={t('common.copy', 'Copy')}
                                        >
                                            {copiedDomain ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                                        </button>
                                    </div>

                                    {/* Value cell */}
                                    <div className="flex items-center gap-2 min-w-0">
                                        <code className="text-xs bg-amber-100 dark:bg-amber-900/50 px-2 py-1 rounded truncate font-mono block w-full">
                                            {dnsChallenge.value}
                                        </code>
                                        <button
                                            type="button"
                                            onClick={() => copyToClipboard(dnsChallenge.value, 'value')}
                                            className="text-amber-500 hover:text-amber-700 dark:hover:text-amber-300 flex-shrink-0 transition-colors"
                                            title={t('common.copy', 'Copy')}
                                        >
                                            {copiedValue ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Polling status indicator */}
                            {certRecord?.status === 'PENDING' && (
                                <div className="flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400">
                                    <Clock className="w-3.5 h-3.5 animate-pulse" />
                                    <span>{t('certificates.dnsChallenge.waiting')}</span>
                                </div>
                            )}

                            {/* Confirm button */}
                            <div className="flex justify-end">
                                <button
                                    type="button"
                                    onClick={handleConfirmDns}
                                    disabled={isConfirmingDns || certRecord?.status !== 'PENDING'}
                                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-amber-600 hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold transition-colors shadow-sm"
                                >
                                    {isConfirmingDns
                                        ? <><Loader2 className="w-4 h-4 animate-spin" /> {t('certificates.dnsChallenge.confirmingButton')}</>
                                        : <><Check className="w-4 h-4" /> {t('certificates.dnsChallenge.confirmButton')}</>
                                    }
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* General: Name, Type, Common Name, SANs */}
                <FormBox title={t('certificates.sections.general')} icon={<Shield className="h-5 w-5" />}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
                        <SimpleTooltip content={t('certificates.tooltips.name') || 'Name for this certificate'} title={t('certificates.fields.name')} position="top" maxWidth="300px">
                            <Label htmlFor="name" required className="text-left">{t('certificates.fields.name')}</Label>
                        </SimpleTooltip>
                        <Input id="name" value={formData.name} onChange={(e) => handleChange('name', e.target.value)} placeholder={t('certificates.placeholders.name')} error={errors.name} />

                        <SimpleTooltip content={t('certificates.tooltips.type') || 'Type of certificate'} title={t('certificates.fields.type')} position="top" maxWidth="300px">
                            <Label htmlFor="type" required className="text-left">{t('certificates.fields.type')}</Label>
                        </SimpleTooltip>
                        <Select value={formData.type} onValueChange={(v) => handleChange('type', v as CertificateType)} options={CERTIFICATE_TYPE_OPTIONS} disabled={isEditMode} />

                        <SimpleTooltip content={t('certificates.tooltips.commonName')} title={t('certificates.fields.commonName')} position="top" maxWidth="300px">
                            <Label htmlFor="commonName" required className="text-left">{t('certificates.fields.commonName')}</Label>
                        </SimpleTooltip>
                        <Input
                            id="commonName"
                            value={formData.commonName}
                            onChange={(e) => handleCommonNameChange(e.target.value)}
                            placeholder="example.com"
                            disabled={isEditMode}
                            error={errors.commonName}
                        />

                        <SimpleTooltip content={t('certificates.tooltips.subjectAltNames')} title={t('certificates.fields.subjectAltNames')} position="top" maxWidth="300px">
                            <Label htmlFor="subjectAltNames" className="text-left">{t('certificates.fields.subjectAltNames')}</Label>
                        </SimpleTooltip>
                        <SanTagInput values={formData.subjectAltNames} onChange={(newSans) => handleChange('subjectAltNames', newSans)} disabled={!!isEditMode} />
                    </div>
                </FormBox>

                {/* Conditional sections by certificate type */}
                {formData.type === 'SELF_SIGNED' && (
                    <CertSelfSignedBox
                        formData={formData}
                        isEditMode={isEditMode}
                        validating={validating}
                        onChange={handleChange}
                        onPreview={handlePreviewSelfSigned}
                    />
                )}
                {formData.type === 'LETS_ENCRYPT' && (
                    <CertLetsEncryptBox
                        formData={formData}
                        errors={errors}
                        onChange={handleChange}
                        commonName={formData.commonName}
                    />
                )}
                {formData.type === 'CUSTOM' && (
                    <CertCustomBox
                        formData={formData}
                        errors={errors}
                        showPrivateKey={showPrivateKey}
                        validating={validating}
                        onChange={handleChange}
                        onTogglePrivateKey={() => setShowPrivateKey(!showPrivateKey)}
                        onValidate={handleValidateCertificate}
                    />
                )}

                {/* ── Error Banner (certbot log) ── */}
                {certRecord?.status === 'ERROR' && certRecord.lastActionError && !certRecord.lastActionError.startsWith('DNS_CHALLENGE:') && (
                    <div className="rounded-xl border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/40 overflow-hidden shadow-sm">
                        <div className="bg-red-100 dark:bg-red-900/50 px-5 py-3 flex items-center gap-3 border-b border-red-200 dark:border-red-700">
                            <span className="text-red-600 dark:text-red-400 font-semibold text-sm">⚠ Error al generar el certificado</span>
                        </div>
                        <pre className="p-4 text-xs text-red-800 dark:text-red-300 overflow-x-auto whitespace-pre-wrap font-mono leading-5 max-h-60 overflow-y-auto">
                            {certRecord.lastActionError}
                        </pre>
                    </div>
                )}



                {certInfo && <CertInfoBox info={certInfo} />}
            </div>

            <FixedActionBar
                onSave={handleSave}
                onCancel={handleCancel}
                onDelete={handleDeleteClick}
                isSaving={isSaving}
                isEditMode={isEditMode}
                cancelText={t('common.cancel')}
                deleteText={t('common.delete')}
            />

            <ConfirmationModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={handleDeleteConfirm}
                title={t('certificates.deleteModal.title')}
                message={t('certificates.deleteModal.message', { name: formData.name })}
                confirmText={t('common.delete')}
                cancelText={t('common.cancel')}
                type="danger"
                isLoading={isDeleting}
            />
        </div>
    );
}
